// Starting missions (G-61, G-72…G-75): one mission at a time on the home system; an open mission jumps
// to where it is done, a done one shows Collect, which pays its Credits and shows the next.
import type { FirstGoal, GalaxyMeResponse } from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Circle } from 'lucide-react';
import { useNavigate } from 'react-router';
import { endpoints } from '../../api/endpoints';
import { queryKeys } from '../../api/queries';
import { formatNumber } from '../../labels';
import { Button, ErrorNotice } from '../ui';
import type { SystemTab } from './types';

const GOALS: Record<FirstGoal, { label: string; hint: string; tab?: SystemTab; path?: string }> = {
  upgrade_building: {
    label: 'Upgrade a building',
    hint: 'Buildings tab — start with the Command Center',
    tab: 'buildings',
  },
  upgrade_storage: {
    label: 'Upgrade a storage',
    hint: 'Full storages stop production',
    tab: 'buildings',
  },
  build_ship: { label: 'Build a ship', hint: 'Shipyard tab', tab: 'shipyard' },
  scout_system: {
    label: 'Scout a nearby system',
    hint: 'Open a system on the map, then Scout',
    path: '',
  },
  raid_unowned: {
    label: 'Raid an unowned system',
    hint: 'Pick a grey system on the map',
    path: '',
  },
  join_alliance: { label: 'Found or join an alliance', hint: 'Alliance tab', path: 'alliance' },
  upgrade_trading_port: {
    label: 'Upgrade the Trading Port',
    hint: 'Buildings tab — more resources per trade',
    tab: 'buildings',
  },
  start_trade: {
    label: 'Start a trade',
    hint: 'Port tab — trade resources for Credits',
    tab: 'port',
  },
  earn_credits: {
    label: 'Earn Credits by trading at the Trading Port',
    hint: 'Port tab — Credits pay for claims',
    tab: 'port',
  },
  claim_unowned: {
    label: 'Claim an unowned system',
    hint: 'Open a grey system on the map, then Claim',
    path: '',
  },
  capture_system: {
    label: 'Capture a claimed system',
    hint: 'Win a battle there once the claim is active',
    path: '',
  },
};

export function FirstGoalsPanel({
  galaxyId,
  firstGoals,
  onTabChange,
}: {
  galaxyId: string;
  firstGoals: GalaxyMeResponse['firstGoals'];
  onTabChange(tab: SystemTab): void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.galaxyMe(galaxyId) });
  const collect = useMutation({
    mutationFn: (goal: FirstGoal) => endpoints.collectMission(galaxyId, goal),
    onSuccess: refresh,
  });
  const { goals, collected, rewardCredits, earnCredits } = firstGoals;
  const current = goals[collected];
  if (!current) return null;
  const g = GOALS[current.id];
  const label =
    current.id === 'earn_credits'
      ? `Earn ${formatNumber(earnCredits.target)} Credits by trading at the Trading Port`
      : g.label;
  return (
    <section
      aria-label="Mission"
      data-mission={current.id}
      className="hud-cut-sm border border-line bg-panel p-3"
    >
      <h2 className="font-display text-xs font-bold uppercase tracking-[0.14em] text-muted">
        Mission {collected + 1}/{goals.length}
      </h2>
      {collect.error ? <ErrorNotice error={collect.error} /> : null}
      <div className="mt-2 flex items-start gap-2 text-sm">
        {current.done ? (
          <Check className="mt-0.5 size-4 shrink-0 text-success" aria-label="done" />
        ) : (
          <Circle className="mt-0.5 size-4 shrink-0 text-faint" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          {current.done ? (
            <span className="text-text">{label}</span>
          ) : (
            <button
              type="button"
              className="text-left text-text hover:text-info"
              onClick={() =>
                g.tab ? onTabChange(g.tab) : navigate(`/g/${galaxyId}${g.path ? `/${g.path}` : ''}`)
              }
            >
              {label} <span className="text-xs text-faint">— {g.hint}</span>
            </button>
          )}
          {current.id === 'earn_credits' && !current.done ? (
            <p className="font-mono text-xs tabular-nums text-info">
              {formatNumber(earnCredits.earned)} / {formatNumber(earnCredits.target)} Credits earned
            </p>
          ) : null}
          <p className="text-xs text-faint">Reward: {formatNumber(rewardCredits)} Credits</p>
        </div>
      </div>
      {current.done ? (
        <Button
          className="mt-3 w-full"
          pending={collect.isPending}
          disabled={collect.isPending}
          onClick={() => collect.mutate(current.id)}
        >
          Collect {formatNumber(rewardCredits)} Credits
        </Button>
      ) : null}
    </section>
  );
}
